"use client"

import type React from "react"
import { createPortal } from "react-dom"
import { useState, useEffect, useRef, useCallback } from "react"
import {
  ArrowBigLeft,
  ArrowBigRight,
  MapPin,
  Heart,
  MessageCircle,
  X,
  Play,
  ChevronUp,
  ChevronDown,
} from "lucide-react"
import gsap from "gsap"
import { useLocale } from 'next-intl'
import { Link } from "@/i18n/navigation"

interface CarouselProps {
  items: {
    id: string
    title?: string
    image: string
    description?: string
    name?: string
    videoUrl?: string
    likes?: number
    comments?: number
    highlighted?: boolean
    slug?: string
  }[]
  showItemsCount?: number
  title?: string
  paragraph?: string
  type?: 'hotel' | 'restaurant' | 'destination' | 'activity' | 'museum' | 'circuit' | 'festivals' | 'other'
}

export function Carousel({ items, showItemsCount = 4.5, title, paragraph, type }: CarouselProps) {
  const locale = useLocale()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showReelsView, setShowReelsView] = useState(false)
  const [currentReelIndex, setCurrentReelIndex] = useState(0)
  const [itemsToShow, setItemsToShow] = useState(showItemsCount)
  const carouselRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const cleanupRef = useRef<(() => void)[]>([])
  const reelsContainerRef = useRef<HTMLDivElement>(null)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])

  // Initialize refs array
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length)
  }, [items.length])

  // Responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      if (typeof window === "undefined") return

      const width = window.innerWidth

      if (width < 640) {
        setItemsToShow(1.1)
      } else if (width < 768) {
        setItemsToShow(1.5)
      } else if (width < 1024) {
        setItemsToShow(2.5)
      } else if (width < 1280) {
        setItemsToShow(3.5)
      } else {
        setItemsToShow(showItemsCount)
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [showItemsCount])

  // Ref callback with proper typing
  const setItemRef = useCallback((el: HTMLDivElement | null, index: number) => {
    itemRefs.current[index] = el
  }, [])

  // GSAP animations for carousel
  const animateCarousel = (direction: "next" | "previous") => {
    if (!carouselRef.current) return

    const isMobile = window.innerWidth < 768
    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024

    // Different animation settings based on device
    const animationSettings = {
      mobile: {
        duration: 0.6,
        ease: "power2.out",
        scale: 0.95,
        y: 20,
      },
      tablet: {
        duration: 0.5,
        ease: "power2.out",
        scale: 0.97,
        y: 10,
      },
      desktop: {
        duration: 0.4,
        ease: "power2.out",
        scale: 0.98,
        y: 5,
      },
    }

    const settings = isMobile
      ? animationSettings.mobile
      : isTablet
        ? animationSettings.tablet
        : animationSettings.desktop

    // Animate current items out
    const tl = gsap.timeline()

    itemRefs.current.forEach((item, index) => {
      if (item && index >= currentIndex && index < currentIndex + Math.ceil(itemsToShow)) {
        tl.to(
          item,
          {
            scale: settings.scale,
            y: direction === "next" ? -settings.y : settings.y,
            opacity: 0.7,
            duration: settings.duration * 0.7,
            ease: settings.ease,
          },
          0,
        )
      }
    })

    // Update state after animation starts
    setTimeout(
      () => {
        if (direction === "next") {
          setCurrentIndex((prev) => (prev >= items.length - Math.ceil(itemsToShow) ? 0 : prev + 1))
        } else {
          setCurrentIndex((prev) => (prev === 0 ? items.length - Math.ceil(itemsToShow) : prev - 1))
        }
      },
      settings.duration * 0.3 * 1000,
    )
  }

  // Animate items in when currentIndex changes
  useEffect(() => {
    if (!carouselRef.current) return

    const isMobile = window.innerWidth < 768
    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024

    const animationSettings = {
      mobile: {
        duration: 0.6,
        stagger: 0.1,
        ease: "back.out(1.2)",
        y: 30,
      },
      tablet: {
        duration: 0.5,
        stagger: 0.08,
        ease: "back.out(1.1)",
        y: 20,
      },
      desktop: {
        duration: 0.4,
        stagger: 0.06,
        ease: "back.out(1.0)",
        y: 15,
      },
    }

    const settings = isMobile
      ? animationSettings.mobile
      : isTablet
        ? animationSettings.tablet
        : animationSettings.desktop

    // Animate new items in
    itemRefs.current.forEach((item, index) => {
      if (item && index >= currentIndex && index < currentIndex + Math.ceil(itemsToShow)) {
        gsap.fromTo(
          item,
          {
            scale: 0.9,
            y: settings.y,
            opacity: 0,
          },
          {
            scale: 1,
            y: 0,
            opacity: 1,
            duration: settings.duration,
            ease: settings.ease,
            delay: (index - currentIndex) * settings.stagger,
          },
        )
      }
    })
  }, [currentIndex, itemsToShow])

  // Hover animations for cards
  useEffect(() => {
    // Clear previous event listeners
    cleanupRef.current.forEach((cleanup) => cleanup())
    cleanupRef.current = []

    // Add new event listeners
    itemRefs.current.forEach((item, index) => {
      if (item && index >= currentIndex && index < currentIndex + Math.ceil(itemsToShow)) {
        // Mouse enter animation
        const handleMouseEnter = () => {
          if (window.innerWidth >= 768) {
            // Only on tablet and desktop
            gsap.to(item, {
              scale: 1.02,
              y: -5,
              duration: 0.3,
              ease: "power2.out",
            })
          }
        }

        // Mouse leave animation
        const handleMouseLeave = () => {
          if (window.innerWidth >= 768) {
            // Only on tablet and desktop
            gsap.to(item, {
              scale: 1,
              y: 0,
              duration: 0.3,
              ease: "power2.out",
            })
          }
        }

        item.addEventListener("mouseenter", handleMouseEnter)
        item.addEventListener("mouseleave", handleMouseLeave)

        // Store cleanup function
        cleanupRef.current.push(() => {
          item.removeEventListener("mouseenter", handleMouseEnter)
          item.removeEventListener("mouseleave", handleMouseLeave)
        })
      }
    })

    // Return cleanup function
    return () => {
      cleanupRef.current.forEach((cleanup) => cleanup())
      cleanupRef.current = []
    }
  }, [currentIndex, itemsToShow])

  const getDetailLink = (item: any) => {
    const slug = item.slug || item.id
    switch (type) {
      case 'hotel':
        return `/hotels/${slug}`
      case 'restaurant':
        return `/restaurants/${slug}`
      case 'destination':
        return `/destinations/${slug}`
      case 'activity':
        return `/activities/${slug}`
      case 'museum':
        return `/museums/${slug}`
      case 'circuit':
        return `/circuits/${slug}`
      case 'festivals':
        return `/festivals/${slug}`  
      default:
        return `/details/${slug}`
    }
  }

  const handlePlayVideo = (clickedIndex: number) => {
    setCurrentReelIndex(clickedIndex)
    setShowReelsView(true)
    document.body.style.overflow = "hidden"
  }

  const handleCloseReels = () => {
    setShowReelsView(false)
    document.body.style.overflow = "auto"
    // Pause all videos when closing
    videoRefs.current.forEach((video) => {
      if (video) video.pause()
    })
  }

  const handleNextReel = () => {
    const videoItems = items.filter((item) => item.videoUrl)
    if (currentReelIndex < videoItems.length - 1) {
      setCurrentReelIndex((prev) => prev + 1)
      scrollToReel(currentReelIndex + 1)
    }
  }

  const handlePreviousReel = () => {
    if (currentReelIndex > 0) {
      setCurrentReelIndex((prev) => prev - 1)
      scrollToReel(currentReelIndex - 1)
    }
  }

  const scrollToReel = (index: number) => {
    if (reelsContainerRef.current) {
      const reelHeight = window.innerHeight
      reelsContainerRef.current.scrollTo({
        top: index * reelHeight,
        behavior: "smooth",
      })
    }
  }

  useEffect(() => {
    if (!showReelsView || !reelsContainerRef.current) return

    const container = reelsContainerRef.current
    let isScrolling: NodeJS.Timeout

    const handleScroll = () => {
      clearTimeout(isScrolling)

      isScrolling = setTimeout(() => {
        const scrollTop = container.scrollTop
        const reelHeight = window.innerHeight
        const newIndex = Math.round(scrollTop / reelHeight)

        if (newIndex !== currentReelIndex) {
          setCurrentReelIndex(newIndex)
        }
      }, 100)
    }

    container.addEventListener("scroll", handleScroll)
    return () => {
      container.removeEventListener("scroll", handleScroll)
      clearTimeout(isScrolling)
    }
  }, [showReelsView, currentReelIndex])

  useEffect(() => {
    if (!showReelsView) return

    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentReelIndex) {
          video.play().catch(() => {})
        } else {
          video.pause()
        }
      }
    })
  }, [currentReelIndex, showReelsView])

  // Close reels on ESC key
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseReels()
      }
    }

    if (showReelsView) {
      document.addEventListener("keydown", handleEscapeKey)
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey)
    }
  }, [showReelsView])

  const handlePrevious = () => {
    animateCarousel("previous")
  }

  const handleNext = () => {
    animateCarousel("next")
  }

  const formatNumber = (num: number) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + "k"
    return num.toString()
  }

  // Touch gestures for mobile
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe) {
      handleNext()
    } else if (isRightSwipe) {
      handlePrevious()
    }
  }

  const videoItems = items.filter((item) => item.videoUrl)

  return (
    <div className="w-full">
      {showReelsView &&
        typeof window !== "undefined" &&
        createPortal(
          <div className="fixed inset-0 z-[9999] bg-black flex">
            <div className="w-16 md:w-20 flex-shrink-0 flex flex-col items-center py-6 gap-6">
              {/* Avatar and Info */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gray-400 flex-shrink-0" />
                {videoItems[currentReelIndex] && (
                  <div className="text-center px-2">
                    <p className="text-white text-xs md:text-sm font-semibold line-clamp-2">
                      {videoItems[currentReelIndex].title}
                    </p>
                    <p className="text-white/70 text-[10px] md:text-xs">Story</p>
                  </div>
                )}
              </div>

              {/* Navigation Arrows - Vertical */}
              <div className="flex-1 flex flex-col justify-center gap-4">
                <button
                  onClick={handlePreviousReel}
                  disabled={currentReelIndex === 0}
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Previous reel"
                >
                  <ChevronUp size={20} className="md:w-6 md:h-6" />
                </button>
                <button
                  onClick={handleNextReel}
                  disabled={currentReelIndex === videoItems.length - 1}
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Next reel"
                >
                  <ChevronDown size={20} className="md:w-6 md:h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 relative flex items-center justify-center">
              {/* Close Button */}
              <button
                onClick={handleCloseReels}
                className="absolute top-4 right-4 z-[10000] w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all hover:scale-110"
                aria-label="Close reels"
              >
                <X size={24} />
              </button>

              {/* Progress Indicator */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {videoItems.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 rounded-full transition-all ${i === currentReelIndex ? "w-8 bg-white" : "w-1 bg-white/50"}`}
                  />
                ))}
              </div>

              {/* Reels Container - Snap scroll */}
              <div
                ref={reelsContainerRef}
                className="h-full w-full overflow-y-scroll snap-y snap-mandatory scroll-smooth"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {videoItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="h-screen w-full snap-start snap-always relative flex items-center justify-center"
                  >
                    {/* Video Player */}
                    <video
                      ref={(el) => {
                        videoRefs.current[index] = el
                      }}
                      src={item.videoUrl}
                      loop
                      playsInline
                      className="h-full w-full object-contain max-w-lg md:max-w-xl lg:max-w-2xl"
                      onClick={(e) => {
                        const video = e.currentTarget
                        if (video.paused) {
                          video.play()
                        } else {
                          video.pause()
                        }
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>

                    {/* Video Info Overlay - Bottom */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none">
                      {/* Location Badge */}
                      {item.name && (
                        <div className="flex items-center gap-2 text-white/90 mb-3">
                          <MapPin size={16} />
                          <span className="text-sm font-medium">{item.name}</span>
                        </div>
                      )}

                      {/* Title */}
                      <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">{item.title}</h3>

                      {/* Description */}
                      {item.description && (
                        <p className="text-white/80 text-sm mb-4 line-clamp-3">{item.description}</p>
                      )}

                      {/* Engagement Stats */}
                      {item.likes !== undefined && item.comments !== undefined && (
                        <div className="flex gap-6 text-white">
                          <span className="flex items-center gap-2">
                            <Heart size={20} className="text-red-400" />
                            <span className="font-semibold">{formatNumber(item.likes)}</span>
                          </span>
                          <span className="flex items-center gap-2">
                            <MessageCircle size={20} className="text-blue-400" />
                            <span className="font-semibold">{formatNumber(item.comments)}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Header Section with Navigation */}
      {(title || paragraph) && (
        <div className="flex justify-between items-end mb-8">
          <div className="flex-1">
            {title && (
              <h2 className="text-4xl font-bold text-foreground mb-3">{title}</h2>
            )}
            {paragraph && (
              <p className="text-muted-foreground hidden md:block text-lg">{paragraph}</p>
            )}
          </div>
          
          {/* Navigation Buttons - Top Right */}
          <div className="flex items-center gap-3 sm:gap-4" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <button
              onClick={locale === 'ar' ? handleNext : handlePrevious}
              className="w-8 h-8 text-primary sm:w-12 sm:h-12 hover:cursor-pointer flex items-center justify-center hover:scale-110 transition-transform"
              aria-label="Previous"
            >
              {locale === 'ar' ? <ArrowBigRight size={40} /> : <ArrowBigLeft size={40} />}
            </button>

            <button
              onClick={locale === 'ar' ? handlePrevious : handleNext}
              className="w-8 h-8 sm:w-12 text-primary sm:h-12 hover:cursor-pointer flex items-center justify-center hover:scale-110 transition-transform"
              aria-label="Next"
            >
              {locale === 'ar' ? <ArrowBigLeft size={40} /> : <ArrowBigRight size={40} />}
            </button>
          </div>
        </div>
      )}

      <div className="relative">
        <div
          className="overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            ref={carouselRef}
            className="flex transition-transform duration-300 ease-out"
            style={{
              transform: locale === 'ar' 
                ? `translateX(${currentIndex * (100 / itemsToShow)}%)` 
                : `translateX(-${currentIndex * (100 / itemsToShow)}%)`,
              direction: locale === 'ar' ? 'rtl' : 'ltr'
            }}
          >
            {items.map((item, index) => (
              <div
                key={item.id}
                className="flex-shrink-0 px-2 sm:px-3"
                style={{ width: `${100 / itemsToShow}%` }}
                ref={(el) => setItemRef(el, index)}
              >
                <div className="h-full">
                  {/* Custom Destination Card */}
                  <Link href={getDetailLink(item)} className="block h-full">
                    <div
                      className={`relative h-64 sm:h-80 md:h-96 rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden group cursor-pointer bg-muted shadow-lg hover:shadow-xl transition-all`}
                      onClick={(e) => {
                        if (item.videoUrl) {
                          e.preventDefault()
                          const videoIndex = videoItems.findIndex((v) => v.id === item.id)
                          handlePlayVideo(videoIndex)
                        }
                      }}
                    >
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                      style={{
                        backgroundImage: `url('${item.image}')`,
                        backgroundPosition: "center",
                      }}
                    >
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
                    </div>

                    {/* Location Badge */}
                    <div className="absolute top-2 sm:top-3 md:top-4 left-2 sm:left-3 md:left-4 z-10 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm">
                      <MapPin size={12} className="sm:w-3 sm:h-3 md:w-4 md:h-4" />
                      <span className="truncate max-w-[80px] sm:max-w-none">{item.name}</span>
                    </div>

                    {/* Content Overlay */}
                    <div className="absolute inset-0 flex flex-col justify-between p-3 sm:p-4 md:p-6">
                      {/* Top Section - Engagement Stats */}
                      <div className="flex justify-end gap-1 sm:gap-2"></div>

                      {/* Bottom Section - Title and Description */}
                      <div className="space-y-1 sm:space-y-2">
                        <h3
                          title={item.title}
                          className="text-md sm:text-xl md:text-lg font-bold text-white drop-shadow-lg line-clamp-1 sm:line-clamp-2"
                        >
                          {item.title}
                        </h3>
                        <p className="text-white/90 text-xs sm:text-sm md:text-base drop-shadow line-clamp-2 sm:line-clamp-3">
                          {item.description}
                        </p>

                        {/* Engagement Metrics */}
                        {item.likes !== undefined && item.comments !== undefined && (
                          <div className="flex gap-2 sm:gap-4 text-white/80 text-xs sm:text-sm pt-1 sm:pt-2 border-t border-white/20">
                            <span className="flex items-center gap-1">
                              <Heart size={12} className="sm:w-3 sm:h-3 md:w-4 md:h-4 text-red-400" />
                              {formatNumber(item.likes)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle size={12} className="sm:w-3 sm:h-3 md:w-4 md:h-4 text-blue-400" />
                              {formatNumber(item.comments)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Hover Play Button */}
                    {item.videoUrl && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg transition-transform hover:scale-110">
                          <Play size={24} className="md:w-7 md:h-7 text-primary ml-0.5" />
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Buttons - Bottom (only show if no title/paragraph) */}
      {!title && !paragraph && (
        <div className="flex justify-center sm:justify-end items-center gap-3 sm:gap-4 mt-4 sm:mt-6 px-2 sm:px-0" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
          <button
            onClick={locale === 'ar' ? handleNext : handlePrevious}
            className="w-8 h-8 text-primary sm:w-12 sm:h-12 hover:cursor-pointer flex items-center justify-center hover:scale-110 transition-transform"
            aria-label="Previous"
          >
            {locale === 'ar' ? <ArrowBigRight size={40} /> : <ArrowBigLeft size={40} />}
          </button>

          <button
            onClick={locale === 'ar' ? handlePrevious : handleNext}
            className="w-8 h-8 sm:w-12 text-primary sm:h-12 hover:cursor-pointer flex items-center justify-center hover:scale-110 transition-transform"
            aria-label="Next"
          >
            {locale === 'ar' ? <ArrowBigLeft size={40} /> : <ArrowBigRight size={40} />}
          </button>
        </div>
      )}
    </div>
  )
}
